import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit, inject, viewChild } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatNativeDateModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import {
  MatPaginator,
  MatPaginatorModule,
  PageEvent,
} from '@angular/material/paginator';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import {
  MatSort,
  MatSortModule,
  Sort,
  SortDirection,
} from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatTooltip, MatTooltipModule } from '@angular/material/tooltip';
import { RouterModule } from '@angular/router';
import { Subject, debounceTime, takeUntil } from 'rxjs';
import {
  UserFeedbackListItem,
  UserFeedbackListRequest,
} from '../../../models/feedback.models';
import { LookupOption, LookupTypeCodes } from '../../../models/lookup.models';
import { FeedbackService } from '../../../services/feedback.service';
import { LookupService } from '../../../services/lookup.service';
import { SnackbarService } from '../../../services/snackbar.service';
import { ErrorStateComponent } from '../../shared/error-state/error-state.component';

@Component({
  selector: 'app-user-feedback',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    RouterModule,
    MatButtonModule,
    MatCardModule,
    MatDatepickerModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatNativeDateModule,
    MatPaginatorModule,
    MatProgressSpinnerModule,
    MatSelectModule,
    MatSortModule,
    MatTableModule,
    MatTooltipModule,
    ErrorStateComponent,
  ],
  templateUrl: './user-feedback.component.html',
  styleUrls: ['./user-feedback.component.scss'],
})
export class UserFeedbackComponent implements OnInit, OnDestroy {
  private feedbackService = inject(FeedbackService);
  private lookupService = inject(LookupService);
  private snackbarService = inject(SnackbarService);
  private fb = inject(FormBuilder);

  readonly paginator = viewChild.required(MatPaginator);
  readonly sort = viewChild.required(MatSort);

  private destroy$ = new Subject<void>();

  // Data
  dataSource = new MatTableDataSource<UserFeedbackListItem>([]);
  displayedColumns: string[] = [
    'dateSubmitted',
    'category',
    'message',
    'pageUrl',
    'userName',
    'userEmail',
  ];

  // Filter options
  feedbackTypes: LookupOption[] = [];

  // State
  isLoading = false;
  error: string | null = null;

  // Pagination
  totalCount = 0;
  pageSize = 25;
  pageIndex = 0;
  pageSizeOptions = [10, 25, 50, 100];

  // Sorting
  sortBy = 'dateSubmitted';
  sortDirection: SortDirection = 'desc';

  // Search/Filter
  filterForm!: FormGroup;
  searchQuery = '';
  private searchDebounceTimer: ReturnType<typeof setTimeout> | null = null;

  ngOnInit(): void {
    this.initializeFilterForm();
    this.loadFeedbackTypes();
    this.loadFeedback();
  }

  ngOnDestroy(): void {
    if (this.searchDebounceTimer) {
      clearTimeout(this.searchDebounceTimer);
    }
    this.destroy$.next();
    this.destroy$.complete();
  }

  private initializeFilterForm(): void {
    // Date filters intentionally start empty so all feedback is shown by default.
    this.filterForm = this.fb.group({
      categories: [[]],
      startDate: [null],
      endDate: [null],
    });

    this.filterForm
      .get('categories')
      ?.valueChanges.pipe(debounceTime(0), takeUntil(this.destroy$))
      .subscribe(() => {
        this.pageIndex = 0;
        this.loadFeedback();
      });
  }

  /** Minimum selectable end date — the chosen start date. */
  get minEndDate(): Date | null {
    return this.filterForm?.get('startDate')?.value ?? null;
  }

  onStartDateChange(): void {
    // Keep the range valid: if the new start date is after the current end
    // date, clear the end date rather than silently querying an empty range.
    const start = this.filterForm.get('startDate')?.value as Date | null;
    const end = this.filterForm.get('endDate')?.value as Date | null;
    if (start && end && start > end) {
      this.filterForm.get('endDate')?.setValue(null, { emitEvent: false });
    }
    this.pageIndex = 0;
    this.loadFeedback();
  }

  onEndDateChange(): void {
    this.pageIndex = 0;
    this.loadFeedback();
  }

  loadFeedbackTypes(): void {
    this.lookupService
      .getLookupOptions(LookupTypeCodes.FEEDBACK_CATEGORY)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (options) => {
          this.feedbackTypes = options;
        },
        error: (error) => {
          console.error('Error loading feedback types:', error);
        },
      });
  }

  loadFeedback(): void {
    this.isLoading = true;
    this.error = null;

    const formValue = this.filterForm.value;
    const categories: string[] = formValue.categories || [];

    const request: UserFeedbackListRequest = {
      page: this.pageIndex + 1,
      pageSize: this.pageSize,
      search: this.searchQuery?.trim() || undefined,
      categories: categories.length > 0 ? categories : undefined,
      startDate: formValue.startDate
        ? this.localDayStartUtc(formValue.startDate)
        : undefined,
      endDate: formValue.endDate
        ? this.localDayEndExclusiveUtc(formValue.endDate)
        : undefined,
      sortBy: this.sortBy,
      sortDirection: this.sortDirection,
    };

    this.feedbackService
      .getUserFeedback(request)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (result) => {
          this.dataSource.data = result.items;
          this.totalCount = result.totalCount;
          this.isLoading = false;
        },
        error: (error) => {
          console.error('Error loading user feedback:', error);
          this.error = 'Failed to load user feedback. Please try again.';
          this.snackbarService.showError('Failed to load user feedback');
          this.isLoading = false;
        },
      });
  }

  onPageChange(event: PageEvent): void {
    this.pageIndex = event.pageIndex;
    this.pageSize = event.pageSize;
    this.loadFeedback();
  }

  onSortChange(sort: Sort): void {
    this.sortBy = sort.active || 'dateSubmitted';
    this.sortDirection = sort.direction || 'desc';
    this.pageIndex = 0;
    this.loadFeedback();
  }

  onSearchInput(): void {
    if (this.searchDebounceTimer) {
      clearTimeout(this.searchDebounceTimer);
    }
    this.searchDebounceTimer = setTimeout(() => {
      this.pageIndex = 0;
      this.loadFeedback();
    }, 400);
  }

  clearSearch(): void {
    this.searchQuery = '';
    this.pageIndex = 0;
    this.loadFeedback();
  }

  resetFilters(): void {
    this.searchQuery = '';

    this.filterForm.reset(
      {
        categories: [],
        startDate: null,
        endDate: null,
      },
      { emitEvent: false },
    );

    // Reset sorting to default (date submitted, newest first). Update MatSort's
    // state directly without emitting sortChange — emitting would trigger
    // onSortChange -> loadFeedback() and fire a second, redundant request on top
    // of the single load() below.
    this.sortBy = 'dateSubmitted';
    this.sortDirection = 'desc';
    const sort = this.sort();
    sort.active = 'dateSubmitted';
    sort.direction = 'desc';

    this.pageIndex = 0;
    this.loadFeedback();
  }

  hasFiltersChanged(): boolean {
    const formValue = this.filterForm.value;
    const sortingChanged =
      this.sortBy !== 'dateSubmitted' || this.sortDirection !== 'desc';

    return !!(
      this.searchQuery?.trim() ||
      (formValue.categories && formValue.categories.length > 0) ||
      formValue.startDate ||
      formValue.endDate ||
      sortingChanged
    );
  }

  /** Enables the message tooltip only when the text is actually truncated. */
  updateTooltipDisabled(el: HTMLElement, tooltip: MatTooltip): void {
    tooltip.disabled = el.scrollWidth <= el.clientWidth;
  }

  formatTimestamp(dateString: string): string {
    if (!dateString) return '';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'Invalid Date';
    return date.toLocaleString();
  }

  /**
   * Start of the user's selected local day, as a UTC ISO instant (inclusive lower
   * bound). Constructing the Date from local y/m/d then calling toISOString()
   * performs the user's-timezone -> UTC conversion, so the backend compares pure
   * instants without any day arithmetic.
   */
  private localDayStartUtc(date: Date): string {
    const d = new Date(date);
    return new Date(
      d.getFullYear(),
      d.getMonth(),
      d.getDate(),
      0,
      0,
      0,
      0,
    ).toISOString();
  }

  /**
   * Start of the day AFTER the user's selected local end day, as a UTC ISO instant
   * (exclusive upper bound). Using the next day exclusively matches the entire end
   * day without the backend needing to add a day.
   */
  private localDayEndExclusiveUtc(date: Date): string {
    const d = new Date(date);
    return new Date(
      d.getFullYear(),
      d.getMonth(),
      d.getDate() + 1,
      0,
      0,
      0,
      0,
    ).toISOString();
  }
}
